import { inject } from "mobx-react";
import * as React from "react";
import { withAnalytics } from "bernie-analytics";
import { AnalyticsStore, Controller, logger } from "bernie-core";
import { Header } from "bernie-global-components";
import {
  ExperimentContext,
  ExtendedContextStore,
  TrackingContext
} from "typings/flexFramework/FlexDefinitions";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { AdvertisingStore } from "stores/AdvertisingStore";
import { FlexFooter } from "components/footer/FlexFooter";
import { Logger } from "bernie-logger";
import { MISSING_CONTEXT_AND_FLEX_VIEWMODEL } from "config/systemEvents";
import { TracingInfo } from "components/footer/TracingInfo";
import RedirectBanner from "components/redirectBanner/RedirectBannerWrapper";
import { MyList } from "components/header/MyList";
import { Notification } from "components/utility/Notification/Notification";
import { doGlassbox } from "components/utility/analytics/FlexAnalyticsUtils";
import { getQueryParamValue } from "components/utility/FeatureFlag/UrlUtils";
import { LocalizedComponentProps } from "bernie-l10n";
import { AdScripts } from "components/utility/Ads/AdScripts";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { AlterLocalizationProvider } from "bernie-alter";
import { CompositionStore } from "stores/CompositionStore";
import { QualtricsScript } from "src/components/utility/Qualtrics/QualtricsScript";
import { BlossomComponents } from "components/flexFramework/BlossomComponents/BlossomComponents";

interface LandingPageProps extends LocalizedComponentProps {
  flexViewModel?: FlexViewModelStore;
  context?: ExtendedContextStore;
  analytics?: AnalyticsStore;
  route?: Controller;
  advertising?: AdvertisingStore;
  compositionStore?: CompositionStore;
}

interface LandingPageStates {
  redirectBanner: React.ReactElement | null;
}

@logger("LandingPage")
@inject("flexViewModel", "context", "analytics", "advertising", "compositionStore")
export default class LandingPage extends React.Component<
  LandingPageProps & { logger?: Logger },
  LandingPageStates
> {
  public componentDidMount() {
    document.title = this.props.compositionStore
      ? this.props.compositionStore.composition.title
      : "";

    // Record user's interactions with the page if enabled
    if (
      this.props.context &&
      this.props.context.experimentContext &&
      this.shouldPerformGlassboxAnalytics(this.props.context.experimentContext)
    ) {
      doGlassbox();
    }
  }

  private shouldPerformGlassboxAnalytics(context?: ExperimentContext): boolean {
    if (!context) {
      return false;
    }

    const forced = getQueryParamValue("forceGlass") === "true";
    const isStorefrontExperimentEnabled =
      context.experiments["Flex_Blossom_Glassbox_Throttle"] === 1;
    const isHTGGlassboxEnabled = context.experiments["Flex_HTG_Blossom_Glass_Throttle"] === 1;

    return isStorefrontExperimentEnabled || isHTGGlassboxEnabled || forced;
  }

  // Prevent the page from being rendered twice
  public shouldComponentUpdate(nextProps: any, nextState: any) {
    return false;
  }

  private addDxPageProps = (): { itemScope: boolean; itemType: string } | null => {
    const { context } = this.props;
    const pageType = context && context.searchContext && context.searchContext.pageType;

    if (pageType !== "Destination-Discovery") {
      return null;
    }

    return {
      itemScope: true,
      itemType: "http://schema.org/LocalBusiness"
    };
  };

  public render() {
    if (!this.props.context || !this.props.flexViewModel || !this.props.compositionStore) {
      this.props.logger!.logEvent(
        MISSING_CONTEXT_AND_FLEX_VIEWMODEL,
        `isMissingContext=${!this.props.context} isMissingFlexViewModel=${!this.props
          .flexViewModel} isMissingCompositionStore=${!this.props.compositionStore}`
      );

      return null;
    }

    const WithAnalyticsFlexComponents = withAnalytics(BlossomComponents);
    const extendedContext: ExtendedContextStore = this.props.context;
    const flexViewModel: FlexViewModelStore = this.props.flexViewModel;
    const compositionStore = this.props.compositionStore;
    const trackingContext: TrackingContext = flexViewModel && flexViewModel.trackingContext;

    if (this.props.advertising && extendedContext.queryParams) {
      this.props.advertising.overrideWithQueryParams(extendedContext.queryParams);
    }

    return (
      <AlterLocalizationProvider>
        <RedirectBanner context={extendedContext} />

        <div className="uitk-grid">
          <Header showLobLinks={true}>
            <MyList />
          </Header>
          <div
            className={`uitk-cell ${extendedContext.searchContext &&
              extendedContext.searchContext.pageType}`}
            {...this.addDxPageProps()}
          >
            <WithAnalyticsFlexComponents
              flexComponents={compositionStore.composition.page.regionList}
              context={extendedContext}
              loading={false}
              analyticsPayload={flexViewModel && flexViewModel.analyticsPayload}
            />
            <Viewport>
              <ViewSmall>
                <FlexFooter
                  footer={flexViewModel.footer}
                  context={extendedContext}
                  isMobile={true}
                />
              </ViewSmall>
              <ViewMedium>
                <FlexFooter
                  footer={flexViewModel.footer}
                  context={extendedContext}
                  isMobile={false}
                />
              </ViewMedium>
            </Viewport>
            <TracingInfo extendedContext={extendedContext} requestId={flexViewModel.requestId} />
          </div>
          <Notification />
        </div>
        <AdScripts pageName={trackingContext.pageName} context={extendedContext} />
        <SchemaOrgScript structuredData={flexViewModel.structuredData} />
        <QualtricsScript context={extendedContext} pageName={trackingContext.pageName} />
      </AlterLocalizationProvider>
    );
  }
}

export { LandingPage };
